import React, { useContext, useEffect, useState } from 'react';

import Paper from '@material-ui/core/Paper';
import { isMobile } from 'react-device-detect';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';

import { GlobalContext } from '../../../../store';
import { IEventEnrollment } from '../../../../store/admin/eventEnrollments/store';
import { IEventDetailedRound } from '../../../../store/admin/eventRounds/store';

import { EventWizardStepTitle } from '../Components/EventWizardStepTitle/EventWizardStepTitle';
import { GroupActions } from './Components/GroupActions/GroupActions';
import { IndividualGroups } from './Components/IndividualGroups/IndividualGroups';
import { PlayerSelector } from './Components/PlayerSelector/PlayerSelector';
import { TeamGroups } from './Components/TeamGroups/TeamGroups';
import { TeamSelector } from './Components/TeamSelector/TeamSelector';
import { TeeSheetCreatorModal } from './Components/TeeSheetCreatorModal/TeeSheetCreatorModal';
import {
  TeeSheetCreatorProvider,
  useTeeSheetCreatorDispatch,
  useTeeSheetCreatorState
} from './Context/TeeSheetCreator.context';
import { onContinue } from './Utils/onContinue';
import { MobileSelectorModal } from './MobileSelectorModal';
import {
  TeamsCreatorProvider,
  useTeamsCreatorDispatch
} from '../TeamsCreator/Context/TeamsCreator.context';
import {
  IObjectById,
  getObjectByIdFromArray
} from '../../../../contrib/Utils/getObjectByIdFromArray';
import { playerSelectorDroppableId } from './Components/PlayerSelector/Utils/playerSelectorDroppableId';

interface IProps {
  clubId: string;
  eventId: string;
  roundId: string;
}

const TeeSheetCreatorComponent = (props: IProps) => {
  const { clubId, eventId, roundId } = props;

  const {
    actions: {
      adminEventRounds: { get: getAdminEventRounds },
      adminEventTeams: { get: getAdminEventTeams },
      notifications: { pushError }
    },
    adminEvents: { current: event },
    adminEventEnrollments,
    adminEventRounds,
    adminRoundFormats
  } = useContext(GlobalContext);

  const { searchValue, filteredGroups } = useTeeSheetCreatorState();

  const {
    actions: { setEnrollments, onPlayerAdd, getShotgunSpots },
    dispatch: teeSheetCreatorDispatch
  } = useTeeSheetCreatorDispatch();

  const [round, setRound] = useState<IEventDetailedRound>();

  const [contestsSelected, setContestsSelected] = useState(false);

  useEffect(() => {
    if (adminRoundFormats[Number(roundId)]) {
      setContestsSelected(
        adminRoundFormats[Number(roundId)].filter(roundFormat => roundFormat.format.isContest)
          .length > 0
      );
    }
  }, [adminRoundFormats, roundId]);

  useEffect(() => {
    getAdminEventTeams(clubId, eventId);
  }, [getAdminEventTeams, clubId, eventId]);

  useEffect(() => {
    if (adminEventRounds[Number(roundId)]) {
      setRound(adminEventRounds[Number(roundId)]);
    }
  }, [adminEventRounds, roundId]);
  const { dispatch: teamsCreatorDispatch } = useTeamsCreatorDispatch();

  useEffect(() => {
    getAdminEventRounds(Number(clubId), Number(eventId), Number(roundId), true);
  }, []);

  useEffect(() => {
    if (round && round.enrollments) {
      setEnrollments(round.enrollments);
    }
  }, [round]);

  useEffect(() => {
    if (round) {
      teeSheetCreatorDispatch({ type: 'filterGroups', value: { round } });
    }
  }, [round, searchValue]);

  useEffect(() => {
    if (eventId && adminEventEnrollments && adminEventEnrollments[Number(eventId)]) {
      const value: IObjectById<IEventEnrollment> = getObjectByIdFromArray<IEventEnrollment>(
        adminEventEnrollments[Number(eventId)]
      );

      teamsCreatorDispatch({
        type: 'setEnrollments',
        value
      });
    }
  }, [adminEventEnrollments[Number(eventId)]]);

  useEffect(() => {
    if (clubId && eventId && roundId) {
      getShotgunSpots(clubId, eventId, roundId).catch();
    }
  }, [clubId, eventId, roundId]);

  const onDragEnd = async (result: DropResult) => {
    const sourceIsPlayerSelector = result.source.droppableId === playerSelectorDroppableId;
    const destinationIsPlayerSelector =
      result.destination && result.destination.droppableId === playerSelectorDroppableId;

    if (sourceIsPlayerSelector && destinationIsPlayerSelector) {
      return;
    }

    if (sourceIsPlayerSelector) {
      const playerEnrollmentId = result.draggableId;
      const groupId = result.destination ? result.destination.droppableId : undefined;

      const foundGroup = filteredGroups.find(group => Number(group.id) === Number(groupId));

      if (foundGroup && round && foundGroup.games.length >= round.playersPerTee) {
        pushError('Group is full');
        return;
      }

      if (groupId) {
        await onPlayerAdd(
          {
            enrollment: Number(playerEnrollmentId),
            seat: foundGroup ? foundGroup.games.length : 4
          },
          Number(groupId)
        );
      }
    }

    // TODO: remove this. This is a hack to get the full round info of the games once a player is
    //       added to the teesheet. That info should come from the backend
    await getAdminEventRounds(Number(clubId), Number(eventId), Number(roundId), true);
  };

  return (
    <div className={`event-wizard-page form round-setup`}>
      <div className={`tee-sheet-creator full-width ${isMobile ? 'mobile' : ''}`}>
        <EventWizardStepTitle
          buttonText="SAVE & CONTINUE"
          buttonOnClick={onContinue(
            clubId,
            eventId,
            roundId,
            contestsSelected ? 'contest' : round && round.order === 0 ? 'flights' : 'launch'
          )}
          title="PAIRINGS AND TEE TIMES"
        />

        <DragDropContext onDragEnd={onDragEnd}>
          <Paper elevation={1} className="paper tee-sheet-container">
            {!isMobile && (event && event.allowTeams ? <TeamSelector /> : <PlayerSelector />)}

            <div className="right">
              <GroupActions />

              <div className="group-list">
                {event && event.allowTeams ? <TeamGroups /> : <IndividualGroups />}
              </div>
            </div>
          </Paper>
        </DragDropContext>

        <TeeSheetCreatorModal />

        {isMobile && <MobileSelectorModal />}
      </div>
    </div>
  );
};

export const TeeSheetCreator = (props: IProps) => (
  <TeeSheetCreatorProvider>
    <TeamsCreatorProvider>
      <TeeSheetCreatorComponent {...props} />
    </TeamsCreatorProvider>
  </TeeSheetCreatorProvider>
);
